import {createSelector} from 'reselect';
import {determineAvailability} from '../utils/determineAvailability';
import {isDatePickerModalOpen} from './modalStates';
import {getListing, getTripData, getDatePicker} from './store';
import {
    getArrivedFromExternalSiteWithoutDates,
    getLocale,
    getSystemClientSideRendered
} from './system';
import getListingPageModalStates from './modalStates';
import {getRateSummary} from './listing';

const getInquiryModalOpen = createSelector(
    [getListingPageModalStates],
    (listingPageModalStates) => {
        return listingPageModalStates.inquiryModalOpen;
    }
);

const getTripDataCurrentDate = createSelector(
    [getTripData],
    (tripData) => {
        return tripData.currentDate;
    }
);

const getDatePickerArriveDateText = createSelector(
    [getDatePicker],
    (datePickerData) => {
        return datePickerData.arriveDateText;
    }
);

const getShouldShowRates = createSelector(
    [getRateSummary],
    (rateSummary) => {
        const hasRates =
            rateSummary &&
            rateSummary.rentNightsConverted &&
            rateSummary.rentNightsConverted.length;

        // We only legally show rates in the calendar for US brands
        const hasUSCurrency = rateSummary && rateSummary.travelerCurrency === 'USD';
        const hasAccurateRate = rateSummary && rateSummary.accurateRate;

        // These could be undefined, which leads to this selector returning undefined instead of a truethy value.
        return !!hasRates && !!hasAccurateRate && !!hasUSCurrency;
    }
);

// Dateless availability data does not depend on arrive/depart date, therefore should only be calculated once on page load
const getDatelessAvailabilityData = createSelector(
    [
        getListing,
        getTripDataCurrentDate,
        getLocale,
        getShouldShowRates,
        getSystemClientSideRendered
    ],
    (listing, tripDataCurrentDate, locale, shouldShowRates, clientSideRendered) => {
        // don't run any of the availability determination logic during SSR for perf reasons, as well as the fact that we defer
        // parsing of the visitor's trip data until the client-side render
        if (!clientSideRendered) {
            return {};
        }

        return determineAvailability({
            listing,
            tripDataCurrentDate,
            selectedArriveDate: '',
            locale,
            shouldShowRates,
            opts: {dateless: true, checkin: false, checkout: false}
        }).dateless;
    }
);

/*
    The calendar, and date pickers, will need to display what days are:
    a) Available for checkin.
    b) Available to checkout on, based on different criteria.
    c) Already occupied.

    The utility to calculate all that data is calculateCalendarAvailabilityData.
    It is extracted in order for other apps and processes to use, and for testing.

    ARE YOU ADDING TO THE SELECTOR DEPENDENCIES?
    Yes?  Only add a dep for individual attributes, and not the whole object.
*/
const getDatePickerAvailabilityData = createSelector(
    [
        isDatePickerModalOpen,
        getListing,
        getTripDataCurrentDate,
        getDatePickerArriveDateText,
        getLocale,
        getSystemClientSideRendered,
        getArrivedFromExternalSiteWithoutDates,
        getInquiryModalOpen
    ],
    (
        datePickerOpen,
        listing,
        tripDataCurrentDate,
        datePickerArriveDateText,
        locale,
        clientSideRendered,
        arrivedFromExternalSiteWithoutDates,
        inquiryModalOpen
    ) => {
        // don't run any of the availability determination logic during SSR for perf reasons, as well as the fact that we defer
        // parsing of the visitor's trip data until the client-side render
        if (!clientSideRendered || !datePickerOpen) {
            return {};
        }

        return determineAvailability({
            listing,
            tripDataCurrentDate,
            selectedArriveDate: datePickerArriveDateText,
            locale,
            shouldShowRates: false,
            useEmptyAvailability: inquiryModalOpen ? false : arrivedFromExternalSiteWithoutDates,
            opts: {dateless: false, checkin: true, checkout: true}
        });
    }
);

const getDatePickerAvailability = createSelector(
    [getDatePickerAvailabilityData, getDatePicker],
    (availability, datePicker) => {
        const datePickerAvailability = datePicker.selectingCheckout
            ? availability.checkout
            : availability.checkin;

        return datePickerAvailability;
    }
);

export default getDatePickerAvailabilityData;
export {
    getDatePickerAvailability,
    getDatePickerAvailabilityData,
    getShouldShowRates,
    getDatelessAvailabilityData
};
